import { useCallback, useContext } from 'react';
import {
  ActivitiesApiContext,
  ActivitiesApiException,
  SearchTaskRequest,
} from '@urbanx/agx.activities.client';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { TaskFilterState } from './TaskFilters/TaskFiltersState';
import {
  createTaskSearchQueryKey,
  restartTaskSearchData,
  type TaskSearchData,
} from './taskSearchQuery';

const PAGE_SIZE = 20;

class InvalidContinuationTokenError extends Error {
  constructor(error: ActivitiesApiException) {
    super(error.message, { cause: error });
  }
}

const createApiSafeArray = <T>(arr?: T[]) => {
  return arr === undefined || arr.length === 0 ? undefined : arr;
};

const buildSearchRequest = (
  taskFilters: TaskFilterState,
  continuationToken?: string,
  fresh?: boolean
): SearchTaskRequest => ({
  pageSize: PAGE_SIZE,
  continuationToken: fresh ? undefined : continuationToken,
  requireQueryCount: fresh,
  states: createApiSafeArray(taskFilters.states),
  teamTypes: createApiSafeArray(taskFilters.teamTypes),
  stages: createApiSafeArray(taskFilters.stages),
  agencyIds: createApiSafeArray(taskFilters.agencyIds),
  assignedToUserIds: createApiSafeArray(taskFilters.assignedToUserIds),
  taskDue: taskFilters.taskDue,
  addressSearchText: taskFilters.addressSearchText,
});

const useTaskSearch = (
  taskFilters?: TaskFilterState,
  filtersPending?: boolean,
  hasNoMatchingAgencies?: boolean,
  searchBlocked?: boolean
) => {
  const api = useContext(ActivitiesApiContext);
  const queryClient = useQueryClient();
  const queryKey = createTaskSearchQueryKey(taskFilters);

  const { data, fetchNextPage, hasNextPage, isFetching, isSuccess } = useInfiniteQuery({
    queryKey,
    initialPageParam: undefined as string | undefined,
    enabled: !!taskFilters && !filtersPending && !hasNoMatchingAgencies && !searchBlocked,
    queryFn: async ({ pageParam, signal }) => {
      if (!taskFilters) throw new Error('Task filters are required');

      try {
        return await api.searchAllTasks(
          buildSearchRequest(taskFilters, pageParam, pageParam === undefined),
          signal
        );
      } catch (error) {
        if (
          pageParam !== undefined &&
          ActivitiesApiException.isActivitiesApiException(error) &&
          error.status === 400
        ) {
          throw new InvalidContinuationTokenError(error);
        }

        throw error;
      }
    },
    retry: (failureCount, error) =>
      !(error instanceof InvalidContinuationTokenError) && failureCount < 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const nextContinuationToken = lastPage.continuationToken;
      if (!nextContinuationToken || nextContinuationToken === lastPageParam) return undefined;

      return nextContinuationToken;
    },
  });

  const refreshSearch = useCallback(() => {
    return queryClient.refetchQueries(
      {
        queryKey: createTaskSearchQueryKey(taskFilters),
        exact: true,
        type: 'active',
      },
      { throwOnError: true }
    );
  }, [queryClient, taskFilters]);

  const refreshSearchInBackground = useCallback(() => {
    return queryClient.refetchQueries(
      {
        queryKey: createTaskSearchQueryKey(taskFilters),
        exact: true,
        type: 'active',
      },
      { throwOnError: false }
    );
  }, [queryClient, taskFilters]);

  const restartSearchInBackground = useCallback(async () => {
    const activeQueryKey = createTaskSearchQueryKey(taskFilters);
    const existingData = queryClient.getQueryData<TaskSearchData>(activeQueryKey);
    if (!existingData) return;

    await queryClient.cancelQueries({ queryKey: activeQueryKey, exact: true });
    queryClient.setQueryData(activeQueryKey, restartTaskSearchData(existingData));
    await queryClient.refetchQueries(
      { queryKey: activeQueryKey, exact: true, type: 'active' },
      { throwOnError: false }
    );
  }, [queryClient, taskFilters]);

  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetching) return;

    void fetchNextPage({ cancelRefetch: false }).then((result) => {
      if (result.error instanceof InvalidContinuationTokenError) {
        void restartSearchInBackground();
      }
    });
  }, [fetchNextPage, hasNextPage, isFetching, restartSearchInBackground]);

  const searchUnavailable = filtersPending || hasNoMatchingAgencies || searchBlocked;
  const tasks = searchUnavailable ? [] : (data?.pages.flatMap((page) => page.tasks ?? []) ?? []);

  return {
    hasMore: !searchUnavailable && !!hasNextPage,
    totalCount:
      hasNoMatchingAgencies || searchBlocked
        ? 0
        : filtersPending
          ? undefined
          : data?.pages[0]?.totalCount,
    tasks,
    searchReady: !searchUnavailable && isSuccess,
    searchAllTasksPending: !searchUnavailable && isFetching,
    loadMore,
    refreshSearch,
    refreshSearchInBackground,
    restartSearchInBackground,
  };
};

export default useTaskSearch;
